
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useScrollRestoration } from "./hooks/use-scroll-restoration";
import { seoKnowledgeBaseData } from "./data/seoKnowledgeBase";
import LeadCaptureModal from "./components/LeadCaptureModal";
import SeoTipsToaster from "./components/SeoTipsToaster";

// Import all pages directly to avoid lazy loading issues
import Index from "./pages/Index";
import About from "./pages/About";
import CaseStudies from "./pages/CaseStudies";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import SeoTool from "./pages/SeoTool";
import Contact from "./pages/Contact";
import KnowledgeBase from "./pages/KnowledgeBase";
import KnowledgeArticle from "./pages/KnowledgeArticle";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Pricing from "./pages/Pricing";
import Dashboard from "./pages/Dashboard";
import Neighborhoods from "./pages/Neighborhoods";
import Neighborhood from "./pages/Neighborhood";
import ThankYou from "./pages/ThankYou";
import SeoQuiz from "./pages/SeoQuiz";
import SeoAssessmentTest from "./pages/SeoAssessmentTest";
import Faqs from "./pages/Faqs";

// Service pages
import LocalSeo from "./pages/services/LocalSeo";
import CompetitiveKeywords from "./pages/services/CompetitiveKeywords";
import EcommerceSeo from "./pages/services/EcommerceSeo";
import SpecializedIndustries from "./pages/services/SpecializedIndustries";
import EnterpriseSeo from "./pages/services/EnterpriseSeo";

// Resource pages
import SeoChecklist from "./pages/resources/SeoChecklist";
import BacklinkBuilding from "./pages/resources/BacklinkBuilding";
import WebsiteSeoSop from "./pages/resources/WebsiteSeoSop";
import GuestPost from "./pages/resources/GuestPost";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (replacing cacheTime)
      retry: 1,
    },
  },
});

// Page visit tracker and scroll management component
const PageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Increment page visit count in localStorage
    const currentCount = parseInt(localStorage.getItem("pageVisitCount") || "0");
    localStorage.setItem("pageVisitCount", String(currentCount + 1));
    
    // Log page views for debugging
    console.log(`Page visited: ${location.pathname}`);
  }, [location.pathname]);
  
  // Use scroll restoration hook
  useScrollRestoration();
  
  return null;
};

const App = () => {
  // Verify that knowledge base data is loaded
  useEffect(() => {
    if (seoKnowledgeBaseData && Array.isArray(seoKnowledgeBaseData)) {
      console.log(`Knowledge base loaded with ${seoKnowledgeBaseData.length} articles`);
    } else {
      console.error("Knowledge base data is not properly loaded!");
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner position="bottom-right" />
        <BrowserRouter>
          <PageTracker />
          <LeadCaptureModal />
          <SeoTipsToaster />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
            <Route path="/seo-tool" element={<SeoTool />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/seo-quiz" element={<SeoQuiz />} />
            <Route path="/seo-assessment-test" element={<SeoAssessmentTest />} />
            <Route path="/faqs" element={<Faqs />} />
            
            {/* Service pages */}
            <Route path="/services/local-seo" element={<LocalSeo />} />
            <Route path="/services/competitive-keywords" element={<CompetitiveKeywords />} />
            <Route path="/services/ecommerce-seo" element={<EcommerceSeo />} />
            <Route path="/services/specialized-industries" element={<SpecializedIndustries />} />
            <Route path="/services/enterprise-seo" element={<EnterpriseSeo />} />
            
            {/* Resource pages */}
            <Route path="/resources/seo-checklist" element={<SeoChecklist />} />
            <Route path="/resources/backlink-101" element={<BacklinkBuilding />} />
            <Route path="/resources/website-seo-sop" element={<WebsiteSeoSop />} />
            <Route path="/resources/choosing-digital-marketing-partner-los-angeles" element={<GuestPost />} />
            
            {/* Neighborhood pages with SEO-optimized URLs */}
            <Route path="/neighborhoods" element={<Neighborhoods />} />
            <Route path="/los-angeles-seo/:id" element={<Neighborhood />} />
            
            {/* Legacy route for backward compatibility */}
            <Route path="/neighborhood/:id" element={<Neighborhood />} />
            
            {/* Dynamic routes for all knowledge base articles with separate checks */}
            {seoKnowledgeBaseData && Array.isArray(seoKnowledgeBaseData) ? (
              seoKnowledgeBaseData.map(article => (
                <Route 
                  key={article.id}
                  path={`/knowledge-base/${article.id}`} 
                  element={<KnowledgeArticle />} 
                />
              ))
            ) : (
              <Route path="/knowledge-base/:id" element={<KnowledgeArticle />} />
            )}
            
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
